import { useCallback, useMemo } from 'react';

import { useSsrApiDataContext } from 'contexts/SsrApiDataContext';
import { getJson } from 'utils/http/getJson';
import { postJson } from 'utils/http/postJson';

import type { UseFetchFnMeta } from './useFetchFn';
import { useFetchFn } from './useFetchFn';

export type FetchUrlOptions = {
  method: 'post';
  body: Record<string, unknown>;
};

type Args<TResponse, TMappedResponse> = {
  url: string | undefined;
  options?: FetchUrlOptions;
  mapResponse: (data: TResponse) => TMappedResponse;
};

/**
 * A wrapper around `useFetchFn` that takes in an `url` and `mapResponse`.
 *
 * This hook uses `SsrApiDataContext` to initialize the cache, this
 * technique allows for transferring data from our Python backend to Hypernova
 * during Server Side Rendering and then hydrating the same data on the client.
 */
export function useFetchUrl<TResponse, TMappedResponse>({
  url,
  options,
  mapResponse,
}: Args<TResponse, TMappedResponse>): [
  TMappedResponse | undefined,
  UseFetchFnMeta<TMappedResponse>,
] {
  const ssrApiData = useSsrApiDataContext();

  const cacheKey = useMemo(() => {
    if (!url) return;

    if (options?.method === 'post') {
      return [url, JSON.stringify(options.body)].filter(Boolean).join('#');
    }

    return url;
  }, [url, options]);

  const initialCacheStorageData = useMemo(() => {
    if (!url) return {};

    const data = ssrApiData[url] as TResponse | undefined;
    if (!data) return {};

    const mappedSsrApiData = mapResponse(data);
    return { [url]: mappedSsrApiData };
  }, [mapResponse, url, ssrApiData]);

  const fetchAndMap = useCallback(
    async (urlToFetch: string | undefined, body?: Record<string, unknown>) => {
      if (typeof urlToFetch === 'undefined') return;

      const response = await (options?.method === 'post'
        ? postJson<TResponse>(urlToFetch, body)
        : getJson<TResponse>(urlToFetch));

      return mapResponse(response);
    },
    [mapResponse, options?.method],
  );

  const [mappedResponse, meta] = useFetchFn({
    fn: fetchAndMap,
    args: [url, options?.body],
    initialCacheStorageData,
    cacheKey,
  });

  return [mappedResponse, meta];
}
